import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, setInMemoryCsrf } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Loader2, AlertCircle } from "lucide-react";

/** Response shape from `POST /api/auth/mfa/setup`. */
interface MfaSetupResponse {
  provisioning_uri: string;
  secret: string;
  qr_data_url: string;
}

interface MfaVerifyResponse {
  ok: boolean;
  backup_codes: string[];
  csrf_token?: string;
}

function isMfaSetupResponse(v: unknown): v is MfaSetupResponse {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return typeof o.provisioning_uri === "string"
    && typeof o.secret === "string" && o.secret.length > 0
    && typeof o.qr_data_url === "string" && o.qr_data_url.startsWith("data:image/");
}

export default function MfaSetup() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [setupData, setSetupData] = useState<MfaSetupResponse | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  const startSetup = async () => {
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post<MfaSetupResponse>("/auth/mfa/setup", {});
      if (!isMfaSetupResponse(data)) {
        console.error("[MFA setup] unexpected response payload", data);
        setErr("MFA setup response was malformed. Please contact support.");
        return;
      }
      setSetupData(data);
    } catch (e) {
      const anyErr = e as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
      const status = anyErr.response?.status;
      const detail = anyErr.response?.data?.detail;
      // Never swallow — always surface something visible.
      const label = formatApiError(detail) || anyErr.message || "Unable to start MFA setup.";
      const msg = status ? `${label} (HTTP ${status})` : label;
      console.error("[MFA setup] request failed", { status, detail, message: anyErr.message });
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post<MfaVerifyResponse>("/auth/mfa/verify", { code });
      if (!data || !Array.isArray(data.backup_codes)) {
        setErr("MFA verify response was malformed. Please retry.");
        return;
      }
      // mfa/verify rotates the CSRF token; update in-memory store so the
      // next cross-origin request carries the fresh token.
      setInMemoryCsrf(data.csrf_token || "");
      setBackupCodes(data.backup_codes);
      toast.success("MFA enabled");
    } catch (e) {
      const anyErr = e as { response?: { status?: number; data?: { detail?: unknown } }; message?: string };
      const status = anyErr.response?.status;
      const label = formatApiError(anyErr.response?.data?.detail) || anyErr.message || "Verification failed.";
      const msg = status ? `${label} (HTTP ${status})` : label;
      console.error("[MFA verify] request failed", { status, detail: anyErr.response?.data?.detail });
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const finalize = async () => {
    await refresh();
    navigate("/");
  };

  if (backupCodes) {
    return (
      <div className="min-h-screen grid place-items-center p-6 bg-slate-50">
        <div className="max-w-lg w-full bg-white border border-slate-200 rounded-lg p-8 shadow-sm" data-testid="mfa-backup-codes">
          <div className="text-emerald-600 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest">
            <ShieldCheck className="w-4 h-4" /> MFA Enabled
          </div>
          <h2 className="text-2xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Manrope" }}>Save your backup codes</h2>
          <p className="text-sm text-slate-500 mt-2">
            Store these in a password manager. Each code works once. They will not be shown again.
          </p>
          <div className="grid grid-cols-2 gap-2 mt-5 font-mono text-sm">
            {backupCodes.map((c) => (
              <div key={c} className="px-3 py-2 border border-slate-200 rounded bg-slate-50" data-testid="backup-code">
                {c}
              </div>
            ))}
          </div>
          <label className="mt-6 flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}
              data-testid="ack-checkbox" />
            I have saved these codes securely.
          </label>
          <Button className="w-full mt-4 rounded-full" disabled={!acknowledged}
            onClick={finalize} data-testid="mfa-finish">
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center p-6 bg-slate-50">
      <div className="max-w-lg w-full bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
        <div className="text-indigo-600 flex items-center gap-2 text-sm font-semibold uppercase tracking-widest">
          <KeyRound className="w-4 h-4" /> Two-factor authentication
        </div>
        <h2 className="text-2xl font-semibold tracking-tight mt-2" style={{ fontFamily: "Manrope" }}>Enable MFA</h2>
        <p className="text-sm text-slate-500 mt-2">
          Scan the QR code with your authenticator app (Google Authenticator, 1Password, Authy)
          and enter the 6-digit code to verify.
        </p>

        {/* Top-level error banner — visible regardless of whether the initial
            `POST /auth/mfa/setup` succeeded. This prevents any silent failure
            of the "Generate QR" click. */}
        {err && (
          <div
            role="alert"
            className="mt-4 flex items-start gap-2 p-3 rounded border border-rose-200 bg-rose-50 text-sm text-rose-700"
            data-testid="mfa-error"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{err}</span>
          </div>
        )}

        {/* Loading indicator visible while the setup request is in flight so the
            "Generate QR" click always produces immediate visual feedback. */}
        {busy && !setupData && (
          <div className="mt-4 flex items-center gap-2 text-sm text-slate-500" data-testid="mfa-busy">
            <Loader2 className="w-4 h-4 animate-spin" /> Contacting server…
          </div>
        )}

        {!setupData && (
          <Button
            onClick={startSetup}
            disabled={busy}
            className="mt-5 rounded-full"
            data-testid="mfa-start"
          >
            {busy ? "Generating…" : "Generate QR"}
          </Button>
        )}

        {setupData && (
          <>
            <div className="mt-5 flex flex-col items-center gap-3">
              <img src={setupData.qr_data_url} alt="MFA QR" className="w-52 h-52 border border-slate-200 rounded"
                data-testid="mfa-qr" />
              <div className="text-xs text-slate-500">Manual key</div>
              <code className="font-mono text-sm bg-slate-50 border border-slate-200 rounded px-3 py-1.5 select-all"
                data-testid="mfa-manual-key">
                {setupData.secret}
              </code>
            </div>
            <form onSubmit={verify} className="mt-6 space-y-3">
              <Label>Verification code</Label>
              <Input inputMode="numeric" pattern="[0-9]*" value={code}
                onChange={(e) => setCode(e.target.value)} required
                data-testid="mfa-code-input" placeholder="123 456" />
              <Button type="submit" disabled={busy} className="w-full rounded-full" data-testid="mfa-verify">
                {busy ? "Verifying…" : "Verify & enable MFA"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
